import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, X, SlidersHorizontal } from "lucide-react";
import { fetchMeta, fetchProducts } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { ProductModal } from "@/components/ProductModal";
import { SplitLines } from "@/components/Reveal";

const Group = ({ title, items, value, onPick, testKey }) => (
  <div>
    <p className="eyebrow mb-4">{title}</p>
    <div className="flex flex-wrap gap-2 lg:flex-col lg:gap-0">
      {items.map((it) => (
        <button key={it.slug} type="button" data-testid={`filter-${testKey}-${it.slug}`} onClick={() => onPick(value === it.slug ? "" : it.slug)} className={`chip lg:!border-0 lg:!bg-transparent lg:!rounded-none lg:!px-0 lg:!py-1.5 lg:justify-between lg:w-full lg:text-left ${value === it.slug ? "active lg:!text-[var(--mint)]" : "lg:!text-[var(--navy)]"}`}>
          <span>{it.label}</span>{it.is_new && <span className="ml-2 h-1.5 w-1.5 rounded-full bg-[var(--mint)]" />}
        </button>
      ))}
    </div>
  </div>
);

export default function Collection() {
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const size = params.get("size") || "", surface = params.get("surface") || "", space = params.get("space") || "", q = params.get("q") || "";
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: fetchMeta });
  const { data: products = [], isLoading } = useQuery({ queryKey: ["products", { size, surface, space, q }], queryFn: () => fetchProducts({ size, surface, space, q }) });

  const update = (k, v) => { const p = new URLSearchParams(params); v ? p.set(k, v) : p.delete(k); setParams(p); };
  const activeCount = [size, surface, space, q].filter(Boolean).length;
  const label = (list, slug) => list?.find((x) => x.slug === slug)?.label;

  return (
    <main data-testid="collection-page" className="bg-[var(--bg)] pt-32 pb-28">
      <div className="container-x">
        <p className="eyebrow mb-6">Our Collection</p>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <SplitLines as="h1" className="font-display text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.02] tracking-tight text-[var(--navy)]" lines={["The full", <>tile <em className="italic text-[var(--mint)]">catalogue.</em></>]} />
          <label className="relative w-full lg:w-80">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="collection-search" className="field !pl-11 !rounded-full" placeholder="Search designs…" value={q} onChange={(e) => update("q", e.target.value)} />
          </label>
        </div>

        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="active-filters">
          <button type="button" data-testid="toggle-filters-btn" onClick={() => setShowFilters((s) => !s)} className="chip lg:hidden"><SlidersHorizontal size={14} className="mr-2" /> Filters</button>
          {size && <button data-testid="active-filter-size" onClick={() => update("size", "")} className="chip active">{label(meta?.sizes, size)} <X size={12} className="ml-2" /></button>}
          {surface && <button data-testid="active-filter-surface" onClick={() => update("surface", "")} className="chip active">{label(meta?.surfaces, surface)} <X size={12} className="ml-2" /></button>}
          {space && <button data-testid="active-filter-space" onClick={() => update("space", "")} className="chip active">{label(meta?.spaces, space)} <X size={12} className="ml-2" /></button>}
          {activeCount > 0 && <button data-testid="clear-filters-btn" onClick={() => setParams({})} className="text-xs font-semibold text-[var(--mint)] ml-2">Clear all</button>}
          <span className="ml-auto font-mono text-[0.62rem] uppercase tracking-[0.22em] text-slate-500" data-testid="results-count">{isLoading ? "Loading…" : `${products.length} designs`}</span>
        </div>

        <div className="mt-10 grid gap-12 lg:grid-cols-12">
          <aside className={`lg:col-span-3 space-y-10 ${showFilters ? "block" : "hidden lg:block"}`} data-testid="filters-sidebar">
            <Group title="By Size" testKey="size" items={meta?.sizes || []} value={size} onPick={(v) => update("size", v)} />
            <Group title="By Surface" testKey="surface" items={meta?.surfaces || []} value={surface} onPick={(v) => update("surface", v)} />
            <Group title="By Space" testKey="space" items={meta?.spaces || []} value={space} onPick={(v) => update("space", v)} />
          </aside>
          <div className="lg:col-span-9">
            {!isLoading && products.length === 0 && (
              <div className="rounded-[1.5rem] border border-dashed border-slate-300 p-16 text-center" data-testid="empty-state">
                <p className="font-display text-2xl text-[var(--navy)]">No designs match these filters.</p>
                <button onClick={() => setParams({})} className="btn-pill btn-ghost mt-6 text-xs">Clear filters</button>
              </div>
            )}
            <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:gap-7" data-testid="products-grid">
              {products.map((p, i) => <ProductCard key={p.slug} product={p} index={i} onOpen={setActive} />)}
            </div>
          </div>
        </div>
      </div>
      <ProductModal product={active} onClose={() => setActive(null)} />
    </main>
  );
}
